import {
  Alert,
  Button,
  Group,
  Image,
  Loader,
  Stack,
  Tabs,
  Text,
} from "@mantine/core"
import { IconAlertCircle, IconEdit } from "@tabler/icons-react"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"

import { BudgetStatus } from "@costory/types/prisma-client"

import { useUser } from "@costory/front/components/auth/UserContext"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { BudgetCSVExportDownloadButton } from "@costory/front/pages/Budgets/components/BudgetCSVExportButton"
import { BudgetNotificationsTab } from "@costory/front/pages/Budgets/components/BudgetNotificationsTab"
import { BudgetTable } from "@costory/front/pages/Budgets/components/BudgetTable"
import { BudgetTableMode } from "@costory/front/pages/Budgets/consts"
import {
  useGetBudgetChartUrlQuery,
  useGetBudgetQuery,
} from "@costory/front/queries/budgets"

export const BudgetPage = () => {
  const { budgetId } = useParams<{ budgetId: string }>()
  const query = useGetBudgetQuery(budgetId!)
  const user = useUser()
  const [activeTab, setActiveTab] = useState<string | null>("trend")

  // Fetch chart URLs through authenticated API
  const trendChartQuery = useGetBudgetChartUrlQuery(budgetId!, "trend")
  const cumulativeChartQuery = useGetBudgetChartUrlQuery(
    budgetId!,
    "cumulative",
  )

  return (
    <QueryWrapper query={query}>
      {({ data: { versions, formData } }) => {
        const hasDraftRevision =
          versions.length !== 1 && versions[0].status === BudgetStatus.DRAFT
        const lastValidated = versions.find(
          (version: { status: string }) =>
            version.status !== BudgetStatus.DRAFT,
        )
        const lastValidatedVersion = lastValidated ?? versions[0]
        const versionsToDisplay = versions.filter(
          (version: { versionId: string }) =>
            version.versionId !== lastValidatedVersion?.versionId,
        )
        return (
          <Stack>
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="trend">Trend</Tabs.Tab>
                <Tabs.Tab value="cumulative">Cumulative</Tabs.Tab>
                <Tabs.Tab value="none">Hide Chart</Tabs.Tab>
                <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
              </Tabs.List>

              {activeTab === "trend" && (
                <Tabs.Panel value="trend" pt="md">
                  {trendChartQuery.isError ? (
                    <Alert
                      icon={<IconAlertCircle size="1rem" />}
                      title="Chart Loading Error"
                      color="red"
                    >
                      Failed to load the trend chart. Please try refreshing the
                      page.
                    </Alert>
                  ) : trendChartQuery.isLoading ? (
                    <Stack align="center">
                      <Loader size="lg" my="xl" type="dots" />
                    </Stack>
                  ) : (
                    <Stack align="center">
                      <Image
                        src={trendChartQuery.data}
                        alt="Budget Trend Chart"
                        radius="md"
                      />
                    </Stack>
                  )}
                </Tabs.Panel>
              )}

              {activeTab === "cumulative" && (
                <Tabs.Panel value="cumulative" pt="md">
                  {cumulativeChartQuery.isError ? (
                    <Alert
                      icon={<IconAlertCircle size="1rem" />}
                      title="Chart Loading Error"
                      color="red"
                    >
                      Failed to load the cumulative chart. Please try refreshing
                      the page.
                    </Alert>
                  ) : cumulativeChartQuery.isLoading ? (
                    <Stack align="center">
                      <Loader size="lg" my="xl" type="dots" />
                    </Stack>
                  ) : (
                    <Stack align="center">
                      <Image
                        src={cumulativeChartQuery.data}
                        alt="Budget Cumulative Chart"
                        radius="md"
                      />
                    </Stack>
                  )}
                </Tabs.Panel>
              )}

              {activeTab === "notifications" && (
                <Tabs.Panel value="notifications" pt="md">
                  <BudgetNotificationsTab budgetId={budgetId!} />
                </Tabs.Panel>
              )}
            </Tabs>

            {activeTab !== "notifications" && (
              <BudgetTable
                formData={formData}
                lastVersion={lastValidatedVersion}
                versionsAvailableToCompare={versionsToDisplay}
                mode={BudgetTableMode.View}
              />
            )}
            {activeTab !== "notifications" && (
              <Group justify="end">
                <BudgetCSVExportDownloadButton
                  budgetId={versions[0].versionId!}
                  data-intercom-target="costory-tour-budget-export-1"
                />
                {versions[0].createdBy.id === user.id && (
                  <>
                    {hasDraftRevision && (
                      <Text>This budget has a draft revision</Text>
                    )}
                    <Button
                      component={Link}
                      to="edit"
                      leftSection={<IconEdit />}
                      variant="outline"
                      data-intercom-target="costory-tour-budget-edit-1"
                    >
                      {hasDraftRevision ? "Edit draft revision" : "Edit"}
                    </Button>
                  </>
                )}
              </Group>
            )}
          </Stack>
        )
      }}
    </QueryWrapper>
  )
}
